import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import UploadAnimation from './UploadAnimation';
import CustomizeAnimation from './CustomizeAnimation';
import DeliveryAnimation from './DeliveryAnimation';

const STEPS = [
  {
    number: 1,
    title: 'Upload your chat',
    subtitle: 'Export your WhatsApp group chat – it only takes a minute',
    Animation: UploadAnimation,
  },
  {
    number: 2,
    title: 'Make it yours',
    subtitle: 'Pick a title, cover, and theme that fits your crew',
    Animation: CustomizeAnimation,
  },
  {
    number: 3,
    title: 'Get it delivered',
    subtitle: 'A printed book full of your funniest moments, shipped to your door',
    Animation: DeliveryAnimation,
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

function StepCard({ step, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const { Animation } = step;

  return (
    <motion.div
      ref={ref}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className="relative bg-parchment/30 rounded-2xl p-6 border border-parchment"
    >
      <div className="absolute -top-3 left-6 w-8 h-8 bg-rust/15 rounded-full flex items-center justify-center">
        <span className="text-sm font-bold text-rust">{step.number}</span>
      </div>

      <div className="h-40 flex items-center justify-center mb-4 mt-2">
        <Animation trigger={isInView} />
      </div>

      <h3 className="font-display text-lg font-semibold text-ink mb-1">{step.title}</h3>
      <p className="text-sm text-sepia">{step.subtitle}</p>
    </motion.div>
  );
}

export default function HowItWorksSection() {
  return (
    <section className="py-16 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-4">
          From chat to book in 3 easy steps
        </h2>
        <p className="text-sepia text-center mb-12 max-w-xl mx-auto">
          From chat export to coffee table book in just a few minutes
        </p>

        <div className="grid sm:grid-cols-3 gap-6">
          {STEPS.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
